/*
    Directive mixin for components that have infinite-scroll-like behavior.

    Fires a "reached-bottom" event when user scrolls to the bottom of the element that 'scrollable' is on.

    A 'heightThresholdAdjust' param is available to adjust the distance which "reached-bottom" is fired at.

    An 'attachToWindow' param is available to detect window scroll (for example, this is true for ClientsList,
    but false for CollateralSelector).

    customParentElement (string) :
    If you want to use a custom parent element for the scrollable element, pass it in here.
    this must be a valid selector. ie: '#my-custom-parent-element' or '.my-custom-parent-element'

    Example:

        v-scrollable="{
          heightThresholdAdjust: 437,
          attachToWindow: false,
          customParentElement: '#clients-list-container'
        }"
 */
import _ from 'lodash';

export default {
  bind(el, binding, vnode) {
    const kwargs = binding.value || {};
    const heightThresholdAdjust = kwargs.heightThresholdAdjust || 0;
    const {
      attachToWindow,
      customParentElement,
      noPixel,
    } = kwargs;

    /** Two cases:
     * 1. Parent is overflown and has a scrollbar: Detect if marker's vertical
     * position is greater than parent bottom + threshold
     * 2. Parent is NOT overflown, doesn't have the scrollbar: Then parent's height
     * will grow together with the list. We detect if the marker appears on the viewport
     */
    const appearedOnBottom = (element) => {
      if (!element) {
        return el.scrollHeight - el.scrollTop - el.clientHeight <= heightThresholdAdjust;
      }

      const rect = element.getBoundingClientRect();
      const parentElement = customParentElement ? element.closest(customParentElement) : element.parentElement;

      // Just preventing console errors.
      if (!parentElement) {
        return false;
      }

      const parentRect = parentElement.getBoundingClientRect();

      const parentHasScroll = parentElement.scrollHeight > parentElement.clientHeight;

      const isVisible = Math.floor(rect.top - heightThresholdAdjust) < Math.ceil(parentRect.bottom);
      const isInsideViewport = rect.top - heightThresholdAdjust
      <= (window.innerHeight || document.documentElement.clientHeight);

      return (parentHasScroll && isVisible)
      || (!parentHasScroll && isInsideViewport);
    };

    binding.$_emit = (eventName) => {
      if (vnode.child) {
        vnode.child.$emit(eventName);
      } else {
        el.dispatchEvent(new Event(eventName));
      }
    };

    // append tiny item to the bottom of the scrollable item
    if (!noPixel) {
      const pixel = document.createElement('div');
      pixel.style.cssText = 'width:1px; height:1px; background-color: transparent;';
      binding.$_pixel = pixel;
      el.appendChild(pixel);
    } else {
      binding.$_pixel = undefined;
    }

    const emitEvent = _.debounce(() => {
      binding.$_emit('reached-bottom');
    }, 400, { leading: true });

    const checkIfVisible = () => {
      const isVisible = appearedOnBottom(binding.$_pixel);
      if (isVisible) {
        emitEvent();
      }
    };

    const onScroll = _.debounce(() => {
      checkIfVisible();
    }, 100);

    el.onmousewheel = onScroll;
    el.onscroll = onScroll;
    el.ontouchmove = onScroll;
    if (attachToWindow) window.onscroll = onScroll;

    // Check once on start
    checkIfVisible();
  },
};
